import { AiReasoningLoader, ChevronDown, ChevronUp } from '@/assets/icons'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { selectConversationTurnProgress, selectConversationWebSocketConnected } from '@/websocket_v2/ws2.selectors'
import clsx from 'clsx'
import { useEffect, useState } from 'react'

type ChatTurnProgressProps = {
  conversationId: string
}

export default function ChatTurnProgress(props: ChatTurnProgressProps) {
  const { conversationId } = props

  const progress = useAppSelector((state: RootState) => selectConversationTurnProgress(state, { conversationId }))
  const isConnected = useAppSelector((state: RootState) => selectConversationWebSocketConnected(state, { conversationId }))
  const [expanded, setExpanded] = useState(false)
  const progressMessages = progress?.messages ?? []
  const isDone = Boolean(progress && !isConnected)

  useEffect(() => {
    if (progressMessages.length === 0) setExpanded(false)
  }, [progressMessages.length])

  if (progress?.hidden) return null

  const latestProgressMessage = progressMessages[progressMessages.length - 1] ?? null
  let progressMessage = latestProgressMessage ?? 'Thinking...'
  if (isDone) progressMessage = 'Done'

  const hasProgressMessages = progressMessages.length > 0
  const progressContentId = `turn-progress-${conversationId}`
  const ProgressChevron = expanded ? ChevronUp : ChevronDown

  const progressRowContent = (
    <>
      <span className="relative h-5 w-5 shrink-0" aria-hidden="true">
        <AiReasoningLoader className="absolute inset-0 h-5 w-5 transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0" />
        <ProgressChevron className="absolute inset-0 h-5 w-5 text-brand-800 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
      </span>
      <span className="min-w-0 flex-1 truncate text-base font-medium leading-6 text-brand-800">{progressMessage}</span>
    </>
  )

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-2 overflow-hidden">
      {hasProgressMessages ? (
        <button
          type="button"
          className={clsx(
            'group flex w-full min-w-0 items-center justify-start gap-2 overflow-hidden border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
            !isDone && 'animate-pulse'
          )}
          aria-controls={progressContentId}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {progressRowContent}
        </button>
      ) : (
        <div className={clsx('flex w-full min-w-0 items-center gap-2 overflow-hidden', !isDone && 'animate-pulse')}>{progressRowContent}</div>
      )}
      {expanded && hasProgressMessages && (
        <ul
          id={progressContentId}
          className="flex w-full min-w-0 list-inside list-disc flex-col gap-2 pl-7 text-sm leading-5 text-brand-neutral-800 marker:text-brand-neutral-600"
        >
          {progressMessages.map((message, index) => (
            <li key={`${index}-${message}`} className="min-w-0 break-words">
              {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
